import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import router, { resetRouter } from '@/router'
import { useUserStoreWithOut } from '@/store/modules/user'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { useMenuStore } from '@/store/modules/menu'

interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  success?: boolean
  msg?: string
}

const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_PATH,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

let isRedirecting = false

function handleLoginExpired() {
  if (isRedirecting) return
  isRedirecting = true

  const userStore = useUserStoreWithOut()
  const permissionStore = usePermissionStoreWithOut()
  const menuStore = useMenuStore()

  // 1. 清本地 token
  localStorage.removeItem('HYschoolToken')
  sessionStorage.removeItem('HYschoolToken')

  // 2. 先重置动态路由
  try {
    resetRouter()
  } catch (e) {
    console.error('重置路由失败', e)
  }

  // 3. 清理 userStore
  try {
    userStore.setToken('')
    userStore.setUserInfo(undefined)
    userStore.setRoleRouters([])
    userStore.setSchoolName('')
    userStore.setSchoolId('')
    userStore.teacherId = ''
  } catch (e) {
    console.error('清理 userStore 失败', e)
  }

  // 4. 清理 permissionStore
  try {
    if (typeof permissionStore.resetPermissionState === 'function') {
      permissionStore.resetPermissionState()
    } else {
      permissionStore.setIsAddRouters(false)
      ;(permissionStore as any).addRouters = []
      ;(permissionStore as any).routers = []
    }
  } catch (e) {
    console.error('重置权限路由失败', e)
  }

  // 5. 清理菜单当前状态
  try {
    menuStore.setPage('')
  } catch (e) {
    console.error('清理菜单状态失败', e)
  }

  // 6. 跳登录页
  router.replace('/login').finally(() => {
    setTimeout(() => {
      isRedirecting = false
    }, 500)
  })
}

request.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = localStorage.getItem('HYschoolToken') || sessionStorage.getItem('HYschoolToken')
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      }
    }

    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    return config
  },
  (error: AxiosError) => {
    ElMessage.error('请求配置错误')
    return Promise.reject(error)
  }
)

request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response

    if (response.config.responseType === 'blob') {
      return response
    }

    if (data.code !== undefined) {
      if (data.code === 200 || data.success === true) {
        return response
      }

      if (!data?.data && data.msg === '该学校没有配置规则') {
        return {
          code: data.code,
          data: 0
        }
      }

      if (data.code === 401) {
        ElMessage.error('登录已过期，请重新登录')
        handleLoginExpired()
        return Promise.reject(data)
      }

      if (data.code === 500) {
        ElMessage.error(data.msg || '服务器内部错误')
        return Promise.reject(data)
      }

      ElMessage.error(data.msg || '请求失败')
      return Promise.reject(data)
    }

    return response
  },
  (error: AxiosError) => {
    if (!error.response) {
      ElMessage.error('网络连接失败，请检查网络')
      return Promise.reject(error)
    }

    const status = error.response.status
    const data: any = error.response.data
    const backendMsg = data?.msg || data?.message || data?.error || data?.errMsg

    if (status === 401) {
      ElMessage.error('登录已过期，请重新登录')
      handleLoginExpired()
      return Promise.reject(error)
    }

    let fallback = '请求失败'
    switch (status) {
      case 400:
        fallback = '请求参数错误'
        break
      case 403:
        fallback = '权限不足'
        break
      case 404:
        fallback = '请求的资源不存在'
        break
      case 500:
        fallback = '服务器内部错误'
        break
      case 502:
        fallback = '网关错误'
        break
      case 503:
        fallback = '服务不可用'
        break
      default:
        fallback = `请求失败 (${status})`
    }

    ElMessage.error(String(backendMsg || fallback))
    return Promise.reject(error)
  }
)

export default request
